import React, { SyntheticEvent } from 'react';
import cx from 'classnames';
import _startCase from 'lodash/startCase';

import { Checkbox } from '@trqk/components';
import { formatDollarsCompact, getProDisplayName } from '@trqk/utils';
import { columnWidths, rowHeight } from '../../../constants';
import { StatementPeriod } from '.';

interface StatementRowProps {
    index: number;
    isNew: boolean;
    roleType: ProRoleType | null;
    selected: boolean;
    setRowState: (updater: unknown) => void;
    statementView: TrqkStatementView;
}

/**
 * Renders the details of a single statementView. Intended for use as a child of a row in
 * ManageStatementsTable.
 * @param {StatementRowProps} props
 */
export const StatementRow = ({
    index,
    isNew,
    roleType,
    selected,
    setRowState,
    statementView,
}: StatementRowProps): JSX.Element => {
    const statementId = statementView?.statement_id ?? null;
    const { pro, region } = statementView;
    const proDisplayName = getProDisplayName(pro, region);
    const displayRoleType =
        roleType === null || roleType === 'unknown' ? '' : _startCase(roleType);

    return (
        <div
            style={{ height: rowHeight }}
            className={cx('flex items-center text-sm pl-2', {
                'bg-highlight-palelavender': index % 2 === 0,
            })}
        >
            <div
                style={{
                    width:
                        columnWidths.expander +
                        columnWidths.nameIndent +
                        columnWidths.name +
                        columnWidths.statements,
                    paddingLeft: columnWidths.nameIndent,
                }}
            >
                {_startCase(statementView?.name ?? '-')}
            </div>
            <div style={{ width: columnWidths.pro }}>{proDisplayName}</div>
            <div style={{ width: columnWidths.role_type }}>
                {displayRoleType}
            </div>
            <div
                className="flex items-center justify-start"
                style={{ width: columnWidths.period }}
            >
                <StatementPeriod
                    isNew={isNew}
                    statementView={statementView}
                    width={columnWidths.period}
                />
            </div>
            <div
                className="pr-1 text-right"
                style={{ width: columnWidths.earnings }}
            >
                {formatDollarsCompact(statementView?.earnings ?? 0)}
            </div>
            <div
                className="flex justify-end pr-1"
                style={{
                    width: columnWidths.spacer,
                }}
            >
                <Checkbox
                    addClasses="respond-to-mouse"
                    checked={selected}
                    color="quinary"
                    inputId={`select.${statementId}`}
                    onChange={(e: SyntheticEvent): void => {
                        const target = e.target as HTMLInputElement;

                        if (statementId) {
                            if (target.checked) {
                                setRowState(
                                    (
                                        prev: ManageStatementsRowState,
                                    ): ManageStatementsRowState => ({
                                        ...prev,
                                        selectedStatementIds: [
                                            ...(prev?.selectedStatementIds ??
                                                []),
                                            statementId,
                                        ],
                                    }),
                                );
                            } else {
                                setRowState(
                                    (
                                        prev: ManageStatementsRowState,
                                    ): ManageStatementsRowState => ({
                                        ...prev,
                                        selectedStatementIds: (
                                            prev?.selectedStatementIds ?? []
                                        ).filter(
                                            (prevId: string): boolean =>
                                                prevId !== statementId,
                                        ),
                                    }),
                                );
                            }
                        }
                    }}
                />
            </div>
        </div>
    );
};
